import type { EnterpriseUserAdminView_ColumnName } from '__generated__/graphql-types';

import type { Column, Learner, LearnerStatus } from 'bundles/enterprise-admin-learners/types';

import _t from 'i18n!nls/enterprise-admin-learners';

export const DEFAULT_LEARNER_STATUSES: LearnerStatus[] = ['JOINED'];

export const LEARNER_SORT_KEY: { [key in keyof Partial<Learner>]: EnterpriseUserAdminView_ColumnName } = {
  removalDate: 'DELETED_AT',
  invitationDate: 'INVITED_AT',
  joinDate: 'JOINED_AT',
  latestActivity: 'LATEST_ACTIVITY_AT',
  name: 'NAME',
  enrollments: 'NUM_ENROLLMENTS',
  completions: 'NUM_COMPLETIONS',
} as const;

export const getLearnerTableColumnOptions = ({ programLevel }: { programLevel: boolean }): Column[] => {
  const programsColumn: Column = {
    accessor: 'programs',
    label: _t('Programs'),
    default: true,
  };
  return [
    {
      accessor: 'name',
      label: _t('Learner name'),
      default: true,
    },
    {
      accessor: 'status',
      label: _t('Status'),
      default: true,
    },
    ...(programLevel ? [] : [programsColumn]),
    {
      accessor: 'invitationDate',
      label: _t('Invitation Date'),
    },
    {
      accessor: 'joinDate',
      label: _t('Join Date'),
      default: true,
    },
    {
      accessor: 'latestActivity',
      label: _t('Latest Activity Date'),
      default: true,
    },
    {
      accessor: 'removalDate',
      label: _t('Removal Date'),
    },
    {
      accessor: 'allowReturn',
      label: _t('Allow return'),
    },
    {
      accessor: 'products',
      label: _t('Current Enrollments'),
    },
    {
      accessor: 'contracts',
      label: _t('Contracts'),
    },
    {
      accessor: 'enrollments',
      label: _t('Enrollments'),
    },
    {
      accessor: 'completions',
      label: _t('Completions'),
    },
    {
      accessor: 'location',
      label: _t('Location'),
    },
    {
      accessor: 'jobTitle',
      label: _t('Job title'),
    },
    {
      accessor: 'jobType',
      label: _t('Job type'),
    },
    {
      accessor: 'managerName',
      label: _t('Manager'),
    },
    {
      accessor: 'businessUnits',
      label: _t('Business units'),
    },
    {
      accessor: 'externalId',
      label: _t('External ID'),
    },
  ] as const;
};

export const getDefaultLearnerTableColumns = ({ programLevel }: { programLevel: boolean }) =>
  getLearnerTableColumnOptions({ programLevel })
    .filter((_) => _.default)
    .map((_) => _.accessor);

export const urlParameterDivider = '~';

export const LEARNER_RESULT_ATTRIBUTES = {
  name: ['RESULT_ATTRIBUTE_NAME', 'RESULT_ATTRIBUTE_EMAIL'],
  joinDate: ['RESULT_ATTRIBUTE_JOINED_AT'],
  invitationDate: ['RESULT_ATTRIBUTE_INVITED_AT'],
  programs: ['RESULT_ATTRIBUTE_PROGRAM_IDS'],
  // "RESULT_ATTRIBUTE_CONTRACT"
  // "RESULT_ATTRIBUTE_PRODUCT_ITEM_IDS"
  enrollments: ['RESULT_ATTRIBUTE_NUM_ENROLLMENTS'],
  completions: ['RESULT_ATTRIBUTE_NUM_COMPLETIONS'],
  externalId: ['RESULT_ATTRIBUTE_EXTERNAL_ID'],
  latestActivity: ['RESULT_ATTRIBUTE_LATEST_ACTIVITY_AT'],
  removalDate: ['RESULT_ATTRIBUTE_DELETED_AT'],
  allowReturn: ['RESULT_ATTRIBUTE_IS_SOFT_DELETED'],
  businessUnits: ['RESULT_ATTRIBUTE_BUSINESS_UNIT', 'RESULT_ATTRIBUTE_BUSINESS_UNIT_2'],
  jobTitle: ['RESULT_ATTRIBUTE_JOB_TITLE'],
  location: ['RESULT_ATTRIBUTE_LOCATION_COUNTRY', 'RESULT_ATTRIBUTE_LOCATION_REGION', 'RESULT_ATTRIBUTE_LOCATION_CITY'],
  managerEmail: ['RESULT_ATTRIBUTE_MANAGER_NAME'],
  managerName: ['RESULT_ATTRIBUTE_MANAGER_EMAIL'],
  jobType: ['RESULT_ATTRIBUTE_JOB_TYPE'],
} as const;

export const CONTROL_GROUP: string[] = [
  'facile-it',
  'galp',
  'allegro',
  'mtn',
  'gcp-ikea',
  'bureauveritas',
  'tfwomenitaly',
  'apc',
  'bbvafundacionmicrofinanzas',
  'pwc',
  'wells-fargo',
  'loyalytics-consulting',
  'william-woods',
  'national-bonds',
  'ubs',
  'virtusa-c4b',
  'cppib',
  'pega',
  'conti-ag',
  'unicomer-caribbean',
  'taqa-group',
  'thales',
  'nbs-bank',
  'grupobafar',
  'kocsistem',
  'padagis-llc',
  'bunq-bv',
  'pgcommunity',
  'johndeere',
  'ford-pd',
  'telefonica-org',
  'dnv',
  'iberdrola-group',
  'technology-resource-center-of-america',
  'albertsons',
  'suncor',
  'gcphsbc',
  'elisa-oyj',
  'sutherland-global',
  'encompass-health',
  'rakuten-japan',
  'collegial',
  'opentec-scotiabank',
  'srib',
  'walmart-gta',
  'pwc-india',
  'harman',
  'bd-it',
  'aptiv',
  'fgf-brands',
  'zurich',
  'investcorp',
  'clevelandclinic',
  'metyis',
  'lloyds-banking-group',
  'edge-group',
  'trolley-convenience-stores',
  'societegenerale',
  'geaerospace',
  'undp',
  'micron',
  'denizbank',
  'lt',
  'workforce-development-center',
  'axis',
  'googlepeopledev',
  'smartstream',
  'sa-tspa',
  'leidos',
  'iadb-scholarships',
  'billups',
  'dlocal',
  'flyadeal',
  'letshego',
  'appier-inc',
  'amplifon',
  'bp',
  'danske-bank',
  'alfanar',
  'stc',
  'nemak',
  'varthana-finance-private-ltd',
  'gwg-ent-year-up',
  'pwc-thailand',
  'onclusive',
  'emiratesnbd',
  'mayo-clinic',
  'remitly',
  'usventure',
  'greensboro-public-library',
  'sony-interactive-entertainment',
  'sandoz',
  'neemba',
  'jsp',
  'telenor',
  'modernatx',
  'bdp',
  'amat',
  'flipkart',
  'orangecsr',
  'mondelez',
  'orange-business',
  'decathlonfrance',
  'grupo-unicomer',
  'alkev-schools-turkey',
  'pgp',
  'doctolib-sas',
  'ooredoo-group',
  'aquila-capital',
  'kaar-technologies',
  'anahuacmexico',
  'aboitizpower',
  'qube-research-and-technologies',
  'currys',
  'nour-net',
  'oracle',
  'adeo',
  'skillhub',
  'exxon',
  'processmaker-inc',
  'bachoco',
  'bsh',
  'walmart-livebetteru',
  'ericsson',
  'oldmutual',
  'apple',
  'adium',
  'tatasteel',
  'kmg-automation',
  'eroski',
  'genpact-c4b',
  'arcelik',
  'bolashak-atyrau',
  'edreams',
  'novartisglobal',
  'kpmg',
  'tatamotors',
  'inscale',
  'secretaria-de-fazenda-rio',
  'crisil-learning',
  'ey',
  'belcorp',
  'portodesantos',
  'xanitos',
  'boubyan-bank',
  'lmco',
  'grupovanti',
  'unvolunteers',
  'best-buy',
  'plexus',
  'amway',
  'xceedance',
  'morganlewis',
  'deloitteindia',
  'samsung-dsa',
  'i-ed-global-llc',
  'tco-kz',
  'dow',
  'hastings-direct',
  'pci',
  'eglobal',
  'tesco',
  'central-securities-depository',
  'coast-capital',
  'ponticelli',
  'ny-times',
  'civil-aviation-academy',
  'raibal-holding',
  'profectus',
  'ascension',
  'sanofiamc',
  'caixabr',
  'bbva',
  'zaptec',
  'pwc-proedge',
  'atlnext',
  'mcdonalds',
  'deutschetelekom',
  'altimetrik',
  'whole-earth-brands',
  'hyundai-motor-india-limited',
  'zentiva',
  'agthia',
  'solutions-plus',
  'ppc',
  'accenture-prod',
  'nordea',
  'gartner-inc-aconp2',
  'swisscom',
  'mufg-investor-services',
  'acid-labs',
  'barrycallebaut',
  'bmo',
  'ford-marketing-sales-services',
  'ajinomotobr',
  'ewec',
  'carmignac',
  'equitable',
  'automatizaciones-industriales-y-de-oficina-de-tlaxcala',
  'jc-penney',
  'paloaltonetworks',
  'leaseplanus',
  'gcp-aes-big-sky',
  'california-department-of-technology',
  'eos-it-solutions-ltd-usa',
  'elements-gs',
  'ebay',
  'fifa',
  'uber-freight',
  'texas-hhs',
  'ecocoursera',
  'income',
  'impacttechinc',
  'soventix-caribbean',
  'venchi-spa',
  'cloudreach',
  'verisk',
  'nri',
  'tam-dev',
  'swixx-biopharma',
  'alliance-bank',
  'veolia-uk',
  'magicleap',
  'tashgeel',
  'fresh-gravity',
  'molitalia-sa',
  'learning-peru',
  'unite-us-inc',
  'varicent-fgth',
  'dunnhumby',
  'recorded-future-inc',
  'boeing-strategy-and-corporate-development',
  'protected',
  'teal-technology-services',
  'sociedade-operacional',
  'acadiahealthcare',
  'kpn',
  'chalhoubuniversity',
  'biogen',
  'metrodigital',
  'euler-hermes',
  'pwc-australia',
  'arup',
  'fti',
  'dll-group-analytics',
  'admapac',
  'gfk-se',
  'fia-staff',
  'contango-management-consultancy-llc',
  'entain',
  'hil',
  'fractal-2',
  'banco-pichincha',
  'lwart',
  'sunrise',
  'babcock-international',
  'totalenergiesbelgium',
  'mdlbeast',
  'instron-apac',
  'traton',
  'ford-it',
  'nacaobrbfla',
  'hiller-measurements-inc',
  'deerfield-management-company-lp',
  'hebmex',
  'mayflower',
  'quintet',
  'thrivin-global',
  'bt',
  'creditsaison-in',
  'brf',
  'seha',
  'spaceneedle',
  'petrorabigh',
  'kkcompany',
  'groupoffice',
  'hid-india-private-limited',
  'sunsure-energy-pvt-ltd',
  'olay-facethestemgap',
  'lookiero-tech-sl',
  'ahad',
  'reliance-family',
  'eldorado',
  'strauss-water',
  'gis',
  'pacifico-seguros-ied',
  'lamresearch',
  'edps',
  'tufuturoseguro',
  'asendia-apac',
  'hudson-bay-capital-management-lp',
  'kroger',
  'signet',
  'takeda-pharmaceutical-mcjbi',
  'prime-it-spain',
  'gotelecomcompany',
  'pan-american-energy',
  'network-management-group-inc',
  'edbi-pte-ltd',
  'luminor',
  'learningedge',
  'waltdisney',
  'ooredooqatar',
  'malaffi-abu-dhabi-health-data-services',
  'be-international-marketing',
  'mlaxiata',
  'levistraussco',
  'decisionsciencegroup',
  'mtnghana',
  'little-creek-casino-resort',
  'nestle-nespresso',
  'ngk-ceramics',
  'b-d',
  'nba',
  'tawal',
  'cdc-amd',
  'fidelity',
  'prysmian-group-0h5gr',
  'arab-monetary-fund',
  'korek-telecom',
  'dewa-admin',
  'better-energy',
  'broadcom',
  'bosch',
  'mtngroup',
  'farmers-insurance-exchange',
  'avtec',
  'sas-sendinblue',
  'swordhealth',
  'axe-finance',
  'chanel',
  'cinemex',
  'campofrio',
  'centro-graduados',
  'hbmx',
  'jasmine-international-public-company-limited',
  'breitling',
  'dropbox-inc',
  'core42',
  'chanel-uk',
  'talentneuron',
  'onward-energy-management-llc',
  'braskem',
  'banco-multiva-sa-institucion-de-banca-multiple',
  'kvb',
  'terrapay',
  'rhbbank',
  'desarrolloprofesionalt',
  'thrive',
  'bandtree-sdn-bhd',
  'bancointernacional',
  'telecel-ghana',
  'kc-doh',
  'institut-pasteur-1',
  'pasha-holding',
  'ibtech22',
  'm42',
  'scb-datax',
  'richemont',
  'aegean',
  'craftsman-technology-group-llc',
  'campus-usa-credit-union',
  'kantar-in',
  'petrobras',
  'equatorial-coca-cola-bottling-company',
  'frontier-management',
  'miahona',
  'trinity-church-wall-street',
  'bank-islam',
  'pttglobal',
  'cardif-bcp-peru',
  'bacardi',
  'synopsys',
  'omers',
  'cardif-cencosud-peru',
  'rakuten-india',
  'offen-petroleum-inc',
  'sppc',
  'anz-4enjl',
  'itc-infotech',
  'cisco-india',
  'mastery-marketplace',
  'exelon',
  'b-tawfiq',
  'aarti-industries',
  'anthem',
  'bajajfinserv',
  'tally-solutions',
  'emaar-misr',
  'dcunited-holdings-llc',
  'seguroscarrefour',
  'goodway-group-inc',
  'dbs',
  'bis',
  'joom',
  'gruppo',
  'cedia-1',
  'gmmco',
  'ficosa',
  'br-rd',
  'kingsalman-internationalairportauthority',
  'teeba',
  'aljomaih',
  'afreximbank',
  'hoistfinance',
  'birlafertility',
  'enovix',
  'earnest-llc',
  'draup',
  'calix',
  'kbtg',
  'east-bay-community-energy',
  'banchile',
  'adesso',
  'lumqad',
  'jll',
  'stepstone',
  'nbe',
  'bice',
  'exim-bank-bh8z7',
  'ica',
  'formacioncourseraath',
  'neosym',
  'bank-danamon',
  'sgsprocurementandrealestate',
  'fordmotor',
  'harrow',
  'o9-solutions',
  'ndb',
  'mtn-cameroon',
  'grit',
  'lutech',
  'brb',
  'akamai',
  'dhl',
  'jfk',
  'servicios-de-capacitacion-cognosonline-spa',
  'cardinalhealth',
  'credit-agricole',
  'etisalategypt',
  'grupoaval',
  'centeredge-software',
  'henkel',
  'seddiqi-holding-llc',
  'impala-terminals',
  'bell-u',
  'bancolombia',
  'kpmggermany',
  'banco-de-credito-del-peru',
  'cardif-crediscotia-peru',
  'amlak-finance-pjsc',
  'craig-international-ltd',
  'pharvaris',
  'verisure',
  'orientcement',
  'edelman',
  'bihl',
  'airudi',
  'ford',
  'fpcl-1',
  'moore-capital-management',
  'partners-group-us',
  'asistenciaseguros',
  'chla',
  'pure-group',
  'liveramp',
  'algorithia',
  'zainkw',
  'ibmz-gsap',
  'solenis',
  'scotiabank-gbme',
  'nextnav-llc',
  'akwa-group-sa',
  'nestle-philippines-inc',
  'airbus',
  'sinarmas-mining',
  'orientpaper',
  'veoliaasia',
  'khan-bank-llc',
  'space-foundation',
  'hubspot-inc',
  'bpcl',
  'mimecast',
  'reyes-coca-cola-bottling',
  'hir-casa',
  'sewa',
  'simoldesplastics',
  'wisetechglobal',
  'ckbh',
  'oenb',
  'osool-asset-management',
  'mercadopago',
  'ericsson-nikola-tesla',
  'vicinity',
  'symphonycommunication',
  'instituto-tecnico-superior-especializado-itse',
  'tonikbank',
  'farm-team',
  'primeit',
  'clarivate-analytics',
  'taos',
  'apellis-pharmaceuticals',
  'egg-digital',
  'pt-bri-manajemen-investasi',
  'bcbssc',
  'intrado-life-safety-inc',
  'sustainable-energy-for-all',
  'americabrb',
  'nationwide',
  'sto',
  'walt-disney-company',
  'cse',
  'nadenso',
  'siemensglobal',
  'cencade',
  'cosmocolor',
  'seguroscajalosandes',
  'liberty-mutual-insurance',
  'onevalley',
  'loadspring-solutions-inc',
  'pharmaccess-bv',
  'ford-bus-sol',
  'amgen',
  'omantel',
  'cardifcontigovida',
  'hulu',
  'microsoft-women-in-cloud',
  'att-mexico',
  'euranova',
  'tonomus',
  'ieso',
  'bc-housing',
  'icici-bank',
  'bnc',
  'totalenergies',
  'emaar',
  'dyouville-university',
  'gulf-bank',
  'ffe',
  'verizon-trial',
  'universidadbdb',
  'medtronic-inc',
  'bcg-thailand',
  'plug-and-play',
  'alj',
  'banque-misr-1',
  'inter-american-development-bank',
  'zainjo',
  'scotiabank-cencosud',
  'kbank',
  'orientelectric',
  'kafaa',
  'jsc-payme',
  'college-of-mount-st-vincent',
  'masdar',
  'att',
  'migros',
  'allsaints',
  'exness-global-limited',
  'cardx',
  'rently',
  'epiroc',
  'zong-china-mobile-pk',
  'innovateq-digital',
  'ge-healthcare-india',
  'utsa',
  'kgisl-edu',
  'mit-adt-university',
  'gwg-ent-universityofcoloradoatboulder',
  'university-of-szeged',
  'sikkim-manipal',
  'uaj',
  'vvce',
  'shree-ameya-public-charitable-trust',
  'southernmiss',
  'bahcesehir',
  'ucc',
  'pristiniinternationalaiuniversity',
  'tamut',
  'kit-c4c',
  'nuc',
  'ent-chitkara-university',
  'xime',
  'marista',
  'oamk',
  'unext-manipal',
  'umb',
  'national-development-council',
  'mmdc',
  'op-jindal-raigarh',
  'kaust-uni',
  'c4c-south-mediterranean-university',
  'det',
  'anahuacoaxaca',
  'universidadpanamericana',
  'orientaluniversity',
  'upj',
  'quinnipiac-university',
  'hec-maroc',
  'astate',
  'newton-school-of-technology',
  'esith',
  'bac-education',
  'chattanooga',
  'franklin',
  'unext-mahe',
  'uni-andalas',
  'uni-pendidikan',
  'sister-nivedita-university',
  'uttyler',
  'vivekananda-global-university',
  'anahuaconline',
  'cetys',
  'srm-university-ap-c4c',
  'iitu',
  'turan-university',
  'centennialcollege',
  'utarlington',
  'oklahoma-state-university-stillwater',
  'mdanderson',
  'unitec-mx',
  'intec-campus',
  'universities-kz',
  'must-university',
  'universidad-uk',
  'certificacionesuabc',
  'the-university-of-texas-at-el-paso',
  'nazarbayev-intellectual-schools-upskill',
  'kaznaru',
  'ramdeobaba-college-of-engineering-management',
  'wtamu',
  'unirazak',
  'unk',
  'christ-university',
  'somaiyavidyavihar',
  'rns-institute',
  'woxsen-university',
  'its',
  'kctc',
  'sribalajiuniversity',
  'udeg',
  'uvmmx',
  'teduniversity',
  'fore-school',
  'king-mongkuts-university',
  'kalinga-institue-of-industrial-technology',
  'trine-university-career-academy',
  'heriotwatt',
  'qataruniversity',
  'itmet',
  'mahidol-university-c4c',
  'jgi',
  'prince-of-songkla-university',
  'ecole-politechnique',
  'udla-academiadocente',
  'swu-university',
  'angelo-state-university',
  'aust',
  'ajmanuniversity',
  'upn',
  'ibn-khaldoun',
  'aui',
  'uoca',
  'rvuniversity',
  'kazakhtelecom',
  'ksiu',
  'hsdt',
  'princesultanuniversity',
  'schoolday',
  'uniminuto',
  'astana-financial-services-authority',
  'liba-online',
  'alamo-colleges-district-acd',
  'lsu',
  'chulalongkorn-gened',
  'vu',
  'enaco',
  'udla',
  'ljuniversity',
  'kamehameha',
  'skct',
  'kumaraguru-college-of-technology',
  'bmg-karaganda-medical-university',
  'universitecadiayyad',
  'morgan-state-university-us',
  'anahuacqueretaro',
  'skyline-university',
  'university-of-prince-mugrin',
  'university-of-immaculate-conception',
  'upeu',
  'scala',
  'c4c-ivey-business-school',
  'mapua',
  'sgi',
  'nl-dalmia-institute',
  'manipal-globalnxt',
  'furman-university',
  'c4c-maryville-college',
  'mcgill-university',
  'vytautas-magnus-university-transform4europe',
  'skcet',
  'upesdehradun',
  'mahe-dubai',
  'charter-oak-state-college',
  'mage-manipal',
  'lmtsm',
  'taylors-institute',
  'rizzo-comercial',
  'thiagarajar-college',
  'unibague',
  'talisisuniversity',
  'university-of-northern-colorado',
  'tsm',
  'westfield-state-university-1',
  'tallinn-university-of-technology-taltech',
  'ruw-bahrain',
  'detroit-catholic-central-high-school',
  'tamuc',
  'ensmr',
  'uautonomayucatan',
  'nmsu',
  'universidad-de-la-empresa-sa',
  'sjsu',
  'universidad-nacional-abierta-y-a-distancia-unad',
  'collin',
  'arizonastateuni',
  'uia',
  'smc',
  'al-mustaqbal-university-college',
  'the-sheikh-zayed-private-academy',
  'ualberta',
  'university-of-oulu-ca',
  'uthscsa',
  'gcic',
  'uni-liechtenstein',
  'esca',
  'chulalongkorn-faculty-of-engineering',
  'unicaribe',
  'c4c-university-of-oklahoma',
  'cavendish-uni',
  'point-university-ca',
  'uemf',
  'second-mile-education',
  'hestim',
  'lewis-university',
  'istanbul-okan-university',
  'shoolini-university',
  'uax',
  'uni-ny',
  'universitas-sumatera-utara',
  'utdallas',
  'aua-manipal',
  'texas-wesleyan-university',
  'central-state-university-ohio',
  'titu-maiorescu-university',
  'khalifa-bin-zayed-al-awal-school',
  'imsuc-ghaziabad',
  'almaarefa',
  'san-francisco-bay-university',
  'ala-too-international-university',
  'ecole-centrale-casablanca',
  'unl',
  'unitec',
  'nsw-education-standards-authority',
  'asu-aznext',
  'ca-kutztown-university-of-pennsylvania',
  'c4c-o-p-jindal-global-university',
  'lincoln',
  'studentrelay',
  'up-education',
  'gwg-sccoe',
  'graduationalliance',
  'connors-state-college-v4ym',
  'boise-state-university',
  'birla-global-university',
  'national-institute-for-nuclear-physics',
  'uom',
  'utsouthwestern',
  'gwg-ent-californiastateuniversity-chico',
  'ccprep-academy',
  'ttu',
  'uofarkansas',
  'covenant-college-us-ga',
  'csusmcoba',
  'brown-university',
  'peoples-university-1',
  'unomaha',
  'itbusinessschool',
  'globallearning',
  'utsystem',
  'yavapai-college',
  'urse',
  'tamiu',
  'unstop',
  'lazarskiuniversity',
  'noun-acetel',
  'mjwsm-syracuse-university',
  'university-of-xalapa',
  'osrhe',
  'ssru',
  'tulane',
  'bml-munjal-university',
  'university-of-arkansas-little-rock',
  'university-of-tartu',
  'austincc',
  'wiley-university',
  'skemabusinessschool',
  'c4cb-university-of-hradec-kralove',
  'university-of-pittsburgh',
  'laureateprocurement',
  'rvim',
  'acounty',
  'prodesp',
  'unhq',
  'nih',
  'pakistan',
  'mhsss-gov',
  'adsg',
  'qazaqstan',
  'ga-dph',
  'infonavit',
  'educate-to-employ',
  'pif',
  'stateofmissouri',
  'nys-comptroller-sga',
  'nadra',
  'dia',
  'human-initiative',
  'world-bank-laos',
  'cael',
  'fmps-morocco',
  'txhhs',
  'detr',
  'cgbqatar',
  'sdaia-academy',
  'guyana-ministry-of-health',
  'adnoc',
  'cbo',
  'dod-ctmo',
  'eaa',
  'capaciti',
  'norges-bank',
  'lanl-isti',
  'gbc',
  'psdf',
  'edutechcostarica',
  'nitda',
  'georgia-technology-authority',
  'barbados-nti',
  'latviannva',
  'inefop',
  'nyc-dohmh-phl',
  'usds-cdp',
  'services-australia-c4g',
  'mofha-gy',
  'mintic-gy',
  'texas-workforce-commission',
  'futurenation-bangladesh',
  'mohap',
  'tahakom',
  'goal-gy',
  'inafocam',
  'bcmh',
  'ura',
  'mossif',
  'zero-crabon',
  'cbc',
  'healthfirst',
  'upwardly-global',
  'ips',
  'fl-doh',
  'futuro-health',
  'sama',
  'rcu-sa',
  'us-asean',
  'smartwork-academy',
  'opm-gy',
  'mcmc',
  'ministryofeducation-gy',
  'c4v-reveillefoundation',
  'ca-hhs-otsi',
  'grenada-ict',
  'tntreasury',
  'mdt-ytepp',
  'bit-gy',
  'lifelong-learning',
  'ncis',
  'national-cryptologic-school',
  'sendai-city',
  'nj-doh',
  'refugeetalenthub',
  'super-int-rd',
  'department-of-the-treasury',
  'nih-eye',
  'nist-mml',
  'ebrd-europeanbank',
  'mps-gy',
  'c4wr-state-of-minnesota',
  'bdf',
  'bal',
  'bcl',
  'mfa',
  'ciat-qq0tt',
  'bbk',
  'save-the-children',
  'sams',
  'cwf',
];
export const TREATMENT_GROUP: string[] = [
  'encora',
  'vhi-academy',
  'bloom-holding',
  'academiamdp',
  'nemera',
  'bankmandiri',
  'gcp-hackensackmeridian',
  'edunet',
  'estee-lauder-companies',
  'cinemo-gmbh',
  'believe',
  'academiadigital',
  'elite-flower-services-inc',
  'oryxenergies',
  'madkour',
  'ibm-research',
  'sarb',
  'unext-learning',
  'schneider-electric',
  'ims-learning-resources',
  'meta',
  'deloitte-ardc',
  'capgemini',
  'universidad-cibus',
  'unicredit',
  'sjvn',
  'mongodb-inc2',
  'nike',
  'applause',
  'maxis-communications',
  'qaracter',
  'trilliant',
  'the-vanguard-group',
  'hlb',
  'east-consulting-engineering-company',
  'trinetix',
  'warnermedia',
  'telconet',
  'bertelsmann',
  'bankofireland',
  'zydus-pharmaceuticals-usa-inc',
  'agh-university-of-krakow',
  'scg-thailand',
  'logitech',
  'mahle',
  'canara-bank',
  'hpe',
  'nequiescool',
  'mcpl',
  'telekom-malaysia',
  'nationalbankofcambodia',
  'osttra',
  'goldman-sachs',
  'barclays',
  'ibm-skillsbuild',
  'danone',
  'benefit-cosmetics',
  'amadeus-india',
  'astreya',
  'carrefourtechteams',
  'synchrony',
  'ista',
  'centogene',
  'efinance-centre',
  'leonardo',
  'cognizant-technology-solutions',
  'ardova-plc',
  'improving-corporate-services-llc',
  'central-retail-corporation',
  'sonda-academy',
  'unioncamerounaisedebrasseries',
  'tophat1',
  'entelchile',
  'eli-lilly',
  'pt-pupuk-indonesia',
  'unum',
  'adobe-scholarships',
  'crc-the1',
  'bukcapacitaciones',
  'sanofi',
  'bhutan-dhi',
  'turvo-inc',
  'modus-create',
  'orange',
  'alturkigroup',
  'debiopharm',
  'boehringer',
  'sattva-media-and-consulting-pvt-ltd',
  'accenture-main',
  'loreal',
  'brightsparks',
  'cirrus-logic-inc',
  'garrettmotion',
  'bnp-paribas-cardif-home',
  'roche',
  'minerva-foods',
  'abg',
  'ly-corp',
  'gavs-technologies',
  'merck',
  'yamaha-motor-company',
  'banco-bpm-spa',
  'booking',
  'toshiba-software-india',
  'dtcc',
  'kms-group',
  'veon',
  'fusion-node-inc',
  'fpt-software',
  'wpp',
  'ltimindtree',
  'bancamifel',
  'national-bank-of-kazakhstan',
  'panasonic',
  'sagemcom-sas',
  'budderfly',
  'genentech',
  'davivienda',
  'maersk',
  'flixbus',
  'santanderuniversidades',
  'bostonscientific',
  'f5',
  'equifax',
  'relx',
  'covestro-deutschland-ag',
  'the-athletic',
  'infogain-india-pvt-ltd',
  'center-for-workforce-inclusion-cwi',
  'toyota-na',
  'marble-box',
  'damu-entrepreneurship-development-fund',
  'zetaglobal',
  'dotdash-meredith',
  'kongsberg-maritime-poland',
  'reliance',
  'target',
  'easyfinancial',
  'rbi',
  'persevere',
  'birlasoft',
  'the-chefs-warehouse-inc',
  'ayala-imi',
  'kazakhstan-housing-company',
  'samsung-eletronica-da-amazonia',
  'au-csl-limited',
  'merieux-universite',
  'alticeacademy',
  'fractal',
  'scitum',
  'npower-canada-2',
  'amazon',
  'pythian-group',
  'pernodricard',
  '#N/A',
  'zs-associates',
  'thiqah',
  'bayer',
  'masterschool',
  'tata-demo',
  'bupa-australia',
  'scotiabankchile',
  'nbg',
  'standardbank',
  'softys',
  'expleo',
  'hvb',
  'itc-group',
  'bradesco',
  'titan-cement',
  'bpce',
  'equinix',
  'three',
  'bnpp',
  'carrier',
  'stmi',
  'play-n-go',
  'kp',
  'tatapower',
  'superbock',
  'perfios',
  'universidad-gabriela-mistral-1',
  'pwc-india-lms',
  'national-womens-soccer-league-llc',
  'finandina',
  'innotech',
  'element-fleet-management',
  'visterra-inc',
  'itausegurotecuido',
  'propertyfinder',
  'mntn',
  'patria',
  'nielsen',
  'nmdc',
  'loreal-women-fund',
  'ogs-india',
  'daman',
  'cgd-xdwq5',
  'qualfon',
  'pak-suzuki',
  'tmtc',
  'celcomdigi',
  'atp',
  'gcp-box',
  'desarrolloprofesionals',
  'garantibank-netherlands',
  'novobanco',
  'honeywell-namer',
  'beeah-group',
  'ng',
  'alkhabeercapital',
  'vanderbilt-international',
  'capco-wipro',
  'mtc-fdoc',
  'te-connectivity',
  'cabify',
  'aicpa',
  'citrofruit',
  'abbott',
  'mimundoprotegido',
  'criteo',
  'pandgrdit',
  'everstream-analytics',
  'oakridge-nl',
  'liverpool',
  'deloitte',
  'u-gie-iris',
  'petroreconcavo',
  'agile-thought',
  'brembo',
  'qfc',
  'ucla-health',
  'rainoil',
  'alansari-holding-co',
  'us-army-logistics-mission-support',
  'rackspace',
  'agea',
  'scotiabank',
  'maker-lab',
  'bayanat-giq-pjsc',
  'nissan',
  'console-connect-hk-limited',
  'acino',
  'decathlon-sports-india-pvt-ltd',
  'welspun',
  'orascom',
  'ltfs',
  'ceres-marine-terminals',
  'hollandandbarrett',
  'impedans-ltd-f787b',
  'childrens-national-medical-center',
  'cardifcolombia',
  'bancogt',
  'cencosudscotiabankchile',
  'naqel-express',
  'bd-swe',
  'trustarc-inc',
  'daralriyadhgroup',
  'woolworths-it',
  'satec',
  'auchanpt',
  'ist-veolia',
  'surecost',
  'cisco-systems',
  'haltonhealthcare',
  'novigi',
  'surainvestments',
  'sbs',
  'itau',
  'banco-internacional-del-peru',
  'jt4',
  'volvocarssoftwareengineering',
  'veolia-latam',
  'nexplore',
  'americana',
  'tecnobit',
  'marlabs',
  'hwz',
  'synacor',
  'galapagos',
  'telcel',
  'verizon',
  'beroe-consulting-india-private-limited',
  'aviancaaprende',
  'sagility',
  'unicredit-germany',
  'astronbuildings',
  'oro-thailand',
  'arconic-corporation',
  'kia-canada-inc',
  'alfasigma-spain-sl',
  'le-moyne-college-1',
  'veriff',
  'ooredoomyanmar',
  'coface-latam',
  'cliftonlarsonallen-llp',
  'experianglobal',
  'rest911',
  'convergent-energy-power-lp',
  'gsk',
  'clairvoyant',
  'bankrakyatindonesia',
  'bancodelnorte',
  'sedco-ksa',
  'coopmego',
  'cmpc',
  'wheels-up-experience-inc',
  'g-research',
  'bnp-paribas-personal-finance',
  'verint-systems-asia-pacific-limited',
  'transporeon',
  'japannext',
  'jala',
  'aker-solutions',
  'liberty-land-abstract-inc',
  'abb-digital',
  'the-vertex-companies',
  'cencade-foundever',
  'scotiabank-codensa',
  'tatadigital',
  'point72',
  'fayetteville-public-library',
  'collectivehealth',
  'tower-research',
  'itaipu',
  'transco',
  'pbb',
  'next',
  'tcpr-development',
  'dayton-job-corps-center',
  'bank-mandiri-consumer-banking',
  'northbay',
  'largo-clean-energy',
  'guardian-group',
  'astroscale',
  'bfl',
  '3m',
  'nei',
  'bell-canada',
  'malomatia-qsc',
  'the-bank-of-saint-lucia-limited',
  'go1',
  'jsw',
  'codellegebysofttek',
  'sace',
  'geb',
  'usaa',
  'pfizerspain',
  'grant-thornton',
  'marutisuzuki',
  'koutfoodgroup',
  'pfs',
  'amx',
  'intense-technologies-ltd',
  'nokia',
  'brit',
  'nvidia',
  'mereo-89-llc',
  'man-truck-and-bus',
  'cognosonline-telecom-argentina',
  'asml',
  'oilmax-energy-private-limited',
  'wataniya-insurance-company-sjsc',
  'rategain',
  'tiktok',
  'mindsprint',
  'alstom',
  'ifc',
  'dubaiprop',
  'jpl',
  'rg-gold',
  'appsinarmas',
  'ritual',
  'my-u-mobile-sdn-bhd',
  'f1rst-tecnologia',
  'crecen-uprm',
  'vodafoneziggo',
  'generali-italia-spa',
  'modo',
  'pierrefabre',
  'niantic',
  'tpp-cadence',
  'ares',
  'eci-telecom-india-private-limited',
  'informatica',
  'banco-sabadell-coursera',
  'clarkston-consulting-inc',
  'orbis-operations-llc',
  'shiseido',
  'ytech',
  'dai',
  'nbb',
  'medable',
  'lbn',
  'iceye',
  'thomsonreuters',
  'karlstorz',
  'mtn-benin',
  'tadawul',
  'woodwing',
  'uwv',
  'commercetools',
  'acioneseuseguro',
  'imec-vzw',
  'pvh',
  'chebanca',
  'icertis-solutions-private-limited',
  'toyotaindus-1',
  'eit-manufacturing',
  'cementos-progreso',
  'motlow-state-cc',
  'kpit',
  'virginmedia',
  'arena-analytics',
  'enpal',
  'koch-business-solutions-india-private-limited',
  'danske-bank-ni',
  'vw-group-academy',
  'cardif-bancofalabella-peru',
  'metso',
  'te-connectivity-ld',
  'tim-hortons-me',
  'ather-energy',
  'nestle-purina',
  'nist-asnm',
  'kemin',
  'assicurazioni-generali-spa',
  'beyond-meat-inc',
  'skillsfuture-singapore',
  'mckinsey',
  'mcit',
  'allkem',
  'capitalone',
  'td-synnex',
  'mtn-cote-divoire',
  'grunenthal',
  'develop-with-dell',
  'snowflake-ip',
  'ligentia',
  'etevers',
  'neurocrine-biosciences',
  'affinity',
  'eurowag',
  'cariad',
  'transunion',
  'cencade-onest-logistics',
  'abb',
  'tam-development-company',
  'omar-kassem-alesayi-holding-group',
  'federal-bank-india',
  'aljazeera',
  'wj-towell',
  'santander-brasil',
  'eva-pharma',
  'ibotta',
  'sesacademy',
  'unicredit-hungary',
  'expo-city-dubai',
  'hudsoninsurancecompany',
  'danieli',
  'scotiabank-2',
  'tencent',
  'prime-it-switzerland',
  'babbel',
  'hertz',
  'gevernova',
  'cognosonline',
  'aditrac-te',
  'kpmg-cardenas-dosal',
  'signify',
  'assetworldcorp-th',
  'cassa-depositi-e-prestiti-spa',
  'qatar-education-above-all',
  'siram',
  'laboratorios-bago',
  'midokura-bdzyq',
  'six-one-commodities-llc',
  'malvernpanalytical',
  'bi4all',
  'cardif-scotiabank-peru',
  'kuvaszsolutions',
  'golearning',
  'nedbank',
  'finalis',
  'axa',
  'cemex',
  'ttecdigital',
  'stcbahrain',
  'fen-bilimleri-okullari',
  'institut-fur-berufliche-bildung-ag-ibb',
  'rea-group',
  'wyndham-hotels',
  'havasmg',
  'emersonelectric',
  'uhgoptum',
  'clinuvel',
  'cardif-cajaarequipa-peru',
  'cmvm',
  'zimmer-biomet',
  'bnpcardif-data-science-challenge',
  'oak-ridge-national-laboratory',
  'etrading-software-limited-philippines',
  'stanford-health-care',
  'volvocarssoftwareengineering',
  'manulife',
  'portaldebeneficios',
  'palc-pfizer',
  'bog',
  'hpe-globalsoft',
  'faurecia-india',
  'juliusbaer',
  'gms-management-solutions',
  'trivago-nv',
  'vwfs-polska',
  'anew-climate1',
  'oil-and-natural-gas-corporation-limited',
  'epi-educacion-profesional-interactiva',
  'saama-technologies',
  'datapipe',
  'a2a',
  'waed',
  'lupin',
  'laborelec-nv',
  'cardif-financieraoh-peru',
  'vidaxl',
  'iec',
  'global-streaming',
  'vital-farms',
  'worldquant',
  'allianzsigorta',
  'bpifrance',
  'nexthink-inc',
  'consensys-2',
  'farasis',
  'massar-solutions',
  'rolandberger',
  'metro-markets',
  'imeg-corp',
  'dow-jones-company-incs',
  'blu-hartmann',
  'moneris',
  'crane-currency-us',
  'govx-inc',
  'idneo',
  'neonseguros',
  'jefferies',
  'almosafer',
  'argos',
  'btpnbank',
  'atpco',
  'campusluzdelsur',
  'telekomsrbija',
  'activate-interactive-pte-ltd',
  'luminus',
  'bbb',
  'bancolombia-estrategias-de-aprendizaje',
  'gig-egypt1',
  'id-me',
  'ckbkolkata',
  'inter-ikea',
  'centigo-ab',
  'nykaa',
  '0014m00001hgyhjqaa',
  'syncro',
  'etihad-water-electricity',
  'eaton',
  'bluescope',
  'cummins',
  'knowledgecity',
  'chartiershoustonschool',
  'itm-skills-university',
  'upc',
  'nehru-arts-and-science-college',
  'istn',
  'marshall',
  'archimedes-academy',
  'princess-sumaya-university',
  'uni-sm',
  'university-of-wollongong-dubai',
  'aou',
  'iberomexico',
  'umkc-bloch',
  'emsi',
  'aiu',
  'allianceuniversity',
  'c4c-hult-international-business-school',
  'ncku',
  'dallas-baptist-university-ca',
  'pcu',
  'uc',
  'universidad-la-salle-mexico-1',
  'psgr-krishnammal-college-for-women',
  'uem-iem',
  'acadeum',
  'jagran-lakecity-university',
  'graphic-era-deemed-to-be-university-dehradun',
  'yenepoya',
  'levelup',
  'wawasan-openuniversity',
  'phenikaa-uni',
  'ppsu',
  'north-caspian-operating-company-nv',
  'ufro',
  'haridwar-university',
  'it-alliance',
  'latech',
  'unc',
  'svec-mbu',
  'sultan-qaboos-university-oman',
  'bay-path-career-academy',
  'kclas',
  'aubh',
  'unab',
  'universidad-del-valle-gt',
  'sadhana-shikshan-mandal-saraswati-college',
  'bangkok-university',
  'mit-vpu',
  'yorkcollegeofpa',
  'northeastern-illinois-university-ca1',
  'cuny-queensborough-community-college',
  'dmiher',
  'odhisa-skill-development-auth',
  'university-of-manitoba',
  'srm-university-c4c',
  'gwg-ent-collegeconsortium',
  'imi',
  'sek-group',
  'dedc',
  'saint-louis-university',
  'american-university-of-phnom-penh',
  'fptuniversity',
  'ua',
  'sdstate',
  'gcu',
  'ubd',
  'van-lan-uni',
  'siu',
  'sagradoedu',
  'mefuniversity',
  'sdnbcollege',
  'amrita-sai-institute-of-science-and-technology',
  'connections-academy-pearson',
  'gedu-college',
  'southeasternuniversity',
  'lanuevaamerica',
  'kkem',
  'latam-universidad-ces',
  'universidadinsurgentes',
  'c4c-mercy-college',
  'formacionduocuc',
  'ubaya',
  'universidad-catolica-de-temuco-faculty',
  'uni-obuda',
  'sdsu-global',
  'mietjmu',
  'dituniversity',
  'tamucc',
  'manuel-s-enverga-university-foundation',
  'manipal',
  'bennettuniversity',
  'northwestern-university',
  'illinoisedu',
  'knu-high',
  'cvmuniversity',
  'vioit',
  'hisarschools',
  'lpu-philippines',
  'gitam',
  'ph-university-of-santo-tomas',
  'uapb',
  'chandigarh-university',
  'skasc',
  'utrgv',
  'university-of-bahrain-c4c',
  'uth-houston',
  'cesa-colegio-de-estudios-superiores-de-administracion',
  'bank-centercredit',
  'cofc',
  'universitas-indonesia',
  'graphic-era-hill-deemed-to-be-university',
  'anahuacmayab',
  'npru',
  'rathinam-tech-campus',
  'alu',
  'amrita',
  'universidad-san-francisco-de-quito',
  'jackson-state-university',
  'imthyderabad',
  'uakron',
  'uts',
  'national-institute-of-engineering',
  'unapec',
  'adtu',
  'mlcu',
  'holy-family-cristo-rey-catholic-high-school-1',
  'saveetha-engineering-college',
  'mtsu',
  'cuc',
  'imdr',
  'deltauni',
  'georgetowncenterglobalimpact',
  'floridapoly',
  'loyola-university-andalusia',
  'wilmu',
  'ggu-outlier',
  'rit',
  'seminole-state-college-92w5',
  'mahendra-educational-institutions',
  'tulsa-community-college-njw8',
  'university-of-science-arts-of-oklahoma-oq9qo',
  'zzz-marquette-university',
  'c-v-raman-global-university',
  'startup-academy',
  'thapar',
  'utmb-health',
  'utpb',
  'csi',
  'nicmar-university-pune',
  'jainuniversity',
  'british-university-hanoi-vietnam',
  'unm',
  'lehren',
  'jklu',
  'gipe',
  'fdu',
  'rhodes-business-school',
  'ecema',
  'iiebm-indus-business-school',
  'cleary',
  'pvusd',
  't5thea',
  'udla-ec',
  'upf',
  'university-of-western-ontario-hr',
  'womens-christian-college',
  'effat-university',
  'yeditepeuniversity',
  'ut-center-at-laredo',
  'umbuenosaires',
  'nmims',
  'audo',
  'lifelonglearning',
  'wvuhealthaffairs',
  'universidad-autonoma-ciudad-juarez',
  'vjti',
  'bowdoin-college',
  'hamptonu',
  'interpuertorico',
  'anahuacjalapa',
  'iit-ip',
  'nwu',
  'ohio-wesleyan-university',
  'european-leadership-university',
  'um6p',
  'mainetechnology',
  'headstart',
  'nsu',
  'tmu',
  'xavier-school-of-management-xlri',
  'manipal-jaipur',
  'anahuacpuebla',
  'csp',
  'hkbu',
  'ufm',
  'cayetano',
  'university-of-doha-for-science-and-technology',
  'vsu',
  'ucdenver',
  'sfasu',
  'vishwakarma-institutes',
  'lagosbusinessschool',
  'pucmm',
  'iirm',
  'hccc',
  'university-of-the-philippines-main',
  'neu-vn',
  'bronx-science-students',
  'smithtechpathways',
  'ahhs',
  'transport-telecommunication-institute',
  'nevadaonline',
  'c4c-st-peters-engineering-college',
  'uir',
  'umw',
  'solvaylifelonglearning',
  'pm4success-international',
  'erciyes',
  'vaughn-college-of-aeronautics-and-technology-1',
  'the-aga-khan-university',
  'universite-numerique-cheikh-hamidou-kane',
  'iua',
  'indonesia-cyber-education',
  'nehru-group-of-institutions',
  'peircecollege',
  'kluniversity',
  'cud-dxb',
  'capella',
  'viot',
  'thiagarajar-college-of-engineering',
  'universidadcooperativadecolombia',
  'carl-albert-state-college-7yeug',
  'dana-indonesia',
  'technological-university-of-pereira',
  'kadir-has-university',
  'university-of-calgary',
  'utexas',
  'institute-of-chartered-accountants-of-pakistan',
  'nitte-educational-institutions',
  'gims',
  'hasanuddin-university',
  'hpu',
  'bsu',
  'uod',
  'oyounacademy',
  'wilmington-college-1',
  'hamptonpwc',
  'state-library-of-north-carolina',
  'state-of-kentucky-cdo',
  'ceibaljap',
  'intan',
  'national-cybersecurity-authority',
  'c4wr-nemisa',
  'futureskills4all',
  'doddau',
  'bpd',
  'bcb',
  'cb-i',
  'enda-inter-arabe',
  'col',
  'azerbaijan-ministry-of-economy',
  'tax-analysts',
  'c4wr-skillsource',
  'skillscommunity',
  'county-of-santa-clara-tss',
  'oecd',
  'cste',
  'nyslabor',
  'gami',
  'kentucky-dph',
  'rung-for-women',
  'it-academy',
  'cdc',
  'scsp',
  'digitaluniversity',
  'laworks',
  'salic',
  'california-state-library',
  'universidades',
  'oitcolombia',
  'department-of-commerce',
  'aba',
  'mpsedc-1',
  'unhcr-staff',
  'nelc',
  'fundacioniqtek',
  'saudi-space-agency',
  'grid-india',
  'south-carolina-dhec-employee-upskill',
  'gii',
  'epo',
  'bdi',
  'oakridge-nl',
  'government-of-the-bahamas',
  'colombia-instituto-nacional-de-salud',
  'llns-it',
  'terre-des-hommes-ch',
  'uth',
  'nycps-trq',
  'ndmc',
  'sef',
  'cbuae',
  'texas-dir',
  'sbf-ksa',
  'edc',
  'tsa',
  'ut-dhss',
  'dubai-chambers',
  'emiratesdevbank',
  'bde',
  'clayutility',
  'united-labor-agency',
  'j-tiu',
  'dubai-municipality-1',
  'us-air-force-digital-university-aetc',
  'mcaa',
  'ministryofagriculture-gy',
  'department-of-health-and-human-services',
  'blm',
  'fcps',
  'sogei',
  'amref',
  'europeanbank',
  'mohre',
  'sanabil',
  'the-mechanical-licensing-collective',
  'cgbqatar-jobseekers',
  'adnoc-globaltrading',
  'ep',
  'financial-conduct-authority',
  'usda-ars',
  'rakgov',
  'state-of-florida-digital-service',
  'ssm',
  'sfc-hong-kong',
  'uspto',
  'joint-force-command-brunssum',
  'mnprowd',
  'dubaipolice',
  'coursera',
  'hooli',
];
